import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { SectionHeader } from "../components/UI";
import { ArrowLeft, Palette, Type, Layout, Save, Globe, Clock } from "lucide-react";

const LS_KEY = "prophr_prefs_v1";

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
  { value: "fr", label: "Français" },
  { value: "de", label: "Deutsch" },
  { value: "ja", label: "日本語" },
  { value: "pt-BR", label: "Português (Brasil)" },
];

const TIMEZONES = [
  "UTC",
  "America/Los_Angeles",
  "America/New_York",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Berlin",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
];

const DEFAULTS = {
  language: (navigator.language || "en").slice(0, 2),
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  density: "comfortable",
  landing: "auto",
  reduce_motion: false,
};

function loadPrefs() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return DEFAULTS;
}

export default function SettingsPreferences() {
  const [prefs, setPrefs] = useState(loadPrefs);

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(prefs));
    // apply density class to <body> for immediate feedback
    document.body.dataset.density = prefs.density;
  }, [prefs]);

  const save = () => {
    localStorage.setItem(LS_KEY, JSON.stringify(prefs));
    toast.success("Preferences saved");
  };

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="settings-preferences">
      <Link to="/settings" className="text-xs text-slate-500 hover:text-slate-900 font-mono flex items-center gap-1 mb-2">
        <ArrowLeft size={12}/> ALL SETTINGS
      </Link>
      <SectionHeader
        title="Preferences"
        subtitle="Fine-tune your PropHR experience. Changes apply immediately on this device."
        right={<button className="btn-primary" onClick={save}><Save size={14} strokeWidth={2}/> Save</button>}
      />

      <div className="space-y-6">
        <Row Icon={Globe} title="Language" body="The interface language for menus, labels, and system messages.">
          <select
            value={prefs.language}
            onChange={(e) => setPrefs({ ...prefs, language: e.target.value })}
            className="input !w-64"
            data-testid="pref-language"
          >
            {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </Row>

        <Row Icon={Clock} title="Timezone" body="Displayed timestamps and SLA due dates use this timezone.">
          <select
            value={prefs.timezone}
            onChange={(e) => setPrefs({ ...prefs, timezone: e.target.value })}
            className="input !w-64"
            data-testid="pref-timezone"
          >
            {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </Row>

        <Row Icon={Layout} title="Density" body="Compact for power users, comfortable for readability.">
          <div className="flex gap-2">
            {["compact", "comfortable", "spacious"].map((d) => (
              <button
                key={d}
                onClick={() => setPrefs({ ...prefs, density: d })}
                className={
                  "px-3 py-1.5 text-xs border transition-colors " +
                  (prefs.density === d ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400")
                }
                data-testid={`pref-density-${d}`}
              >{d}</button>
            ))}
          </div>
        </Row>

        <Row Icon={Type} title="Landing after sign-in" body="Where PropHR takes you when you sign in.">
          <select
            value={prefs.landing}
            onChange={(e) => setPrefs({ ...prefs, landing: e.target.value })}
            className="input !w-64"
            data-testid="pref-landing"
          >
            <option value="auto">Auto (role default)</option>
            <option value="dashboard">Dashboard</option>
            <option value="cases">Cases</option>
            <option value="policies">Policies</option>
            <option value="tickets">Support desk</option>
          </select>
        </Row>

        <Row Icon={Palette} title="Reduce motion" body="Minimize animations and transitions across the app.">
          <Toggle
            checked={prefs.reduce_motion}
            onChange={(v) => setPrefs({ ...prefs, reduce_motion: v })}
            testid="pref-reduce-motion"
          />
        </Row>
      </div>
    </div>
  );
}

function Row({ Icon, title, body, children }) {
  return (
    <div className="border border-slate-200 bg-white p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 border border-slate-200 bg-slate-50 flex items-center justify-center shrink-0">
          <Icon size={16} strokeWidth={1.75} className="text-slate-700"/>
        </div>
        <div>
          <div className="font-display text-base font-semibold text-slate-900">{title}</div>
          <div className="text-xs text-slate-500 mt-0.5 max-w-md">{body}</div>
        </div>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={
        "w-11 h-6 flex items-center rounded-full border transition-colors " +
        (checked ? "bg-slate-900 border-slate-900 justify-end" : "bg-white border-slate-300 justify-start")
      }
    >
      <span className={"w-5 h-5 rounded-full mx-0.5 shadow " + (checked ? "bg-white" : "bg-slate-300")}/>
    </button>
  );
}
